import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CoreChip from "../../../app/core/components/CoreChip/CoreChip";
import CoreSpinner from "../../../app/core/components/CoreSpinner/CoreSpinner";
import UserFilter from "../../../app/core/components/UserFilter/UserFilter";
import { TABLE_ROWS_NUMBER } from "../../../utils/constants/tables";
import { labelForFilterPractice } from "../../../utils/functions/filters";
import {
  useGetPracticeManagementQuery,
  useGetPracticesServicesQuery,
} from "../../api/admin/gestisciPraticheApi";
import { useGetUsersQuery } from "../../api/admin/gestisciUtentiApi";
import practiceManagementSlice from "./GestisciPraticheSlice";
import GestisciPraticaButtonCockpit from "./GestisciPraticheTable/ControlCockPit/GestisciPraticaButtonCockpit";
import GestisciPraticaFilter from "./GestisciPraticheTable/ControlCockPit/GestisciPraticaFilter";
import GestionePraticheTable from "./GestisciPraticheTable/Table/GestisciPraticheTable";
import { useGetStudiosQuery } from "../../api/admin/gestisciStudioApi";
import { useGetCommercialistaQuery } from "../../api/admin/gestisciCommercialistiApi";
import { useGetCompaniesQuery } from "../../api/admin/adminCompany";
import { t } from "i18next";
import { useLocation, useParams } from "react-router-dom";
import CoreBack from "../../../app/core/components/CoreBack/CoreBack";

const GestisciPratichePage = () => {
  const location = useLocation();
  const { id } = useParams();
  const USER_FROM_LOCATION = { id: id, name: location.state, surname: "" };
  const role = useSelector((state) => state.app.role);
  const showAll = role === "Admin" ? true : false;
  const [debouncedInputStudioValue, setDebouncedInputStudioValue] =
    useState("");
  const [selectedUser, setSelectedUser] = useState(
    USER_FROM_LOCATION.id ? USER_FROM_LOCATION : ""
  );
  const [debouncedInputUserValue, setDebouncedInputUserValue] = useState("");
  const [debouncedInputOperatorValue, setDebouncedInputOperatorValue] =
    useState("");
  const [debouncedInputCompanyValue, setDebouncedInputCompanyValue] =
    useState("");
  const [page, setPage] = useState({
    currentPage: 1,
    startRow: 0,
    endRow: TABLE_ROWS_NUMBER,
  });
  const [filter, setFilter] = useState({
    status: "",
    serviceId: "",
    studioId: "",
    adminId: "",
    companyId: "",
  });
  const [orderDirection, setOrderDirection] = useState("desc");
  const [valueToOrderBy, setValueToOrderBy] = useState("createdAt");

  const {
    data: practicesData,
    isLoading: practicesLoading,
    isFetching: practicesFetching,
    refetch,
  } = useGetPracticeManagementQuery({
    page,
    userFilter: selectedUser
      ? `${selectedUser?.name} ${selectedUser?.surname}`
      : "",
    filter,
    orderDirection,
    valueToOrderBy,
    showAll,
  });

  const {
    data: usersData,
    isLoading: isUsersLoading,
    isFetching: isUsersFetching,
  } = useGetUsersQuery({
    page: { startRow: 0, endRow: 10 },
    value: debouncedInputUserValue,
  });
  const { data: operatorsData } = useGetCommercialistaQuery({
    page: { startRow: 0, endRow: 10 },
    value: debouncedInputOperatorValue,
  });
  const {
    data: companiesList,
    isLoading: isCompanySelectLoading,
    isFetching: isCompanySelectFetching,
  } = useGetCompaniesQuery({
    startRow: 0,
    endRow: 10,
    query: debouncedInputCompanyValue,
  });
  const { data: practiceServicesData, isLoading: loadingService } =
    useGetPracticesServicesQuery();
  const { data: adminStudio } = useGetStudiosQuery({
    ...{ currentPage: 1, startRow: 0, endRow: 5 },
    value: debouncedInputStudioValue,
  });
  // Hooks
  const dispatch = useDispatch();

  const handleRemoveFilter = (f) => {
    let newFilter = { ...filter };
    Object.entries(newFilter).forEach(([k, v]) => {
      if (v === f) newFilter[k] = "";
    });
    setFilter(newFilter);
    dispatch(practiceManagementSlice.actions.setTableFilters(newFilter));
  };

  const handleRefecthDocuments = () => {
    refetch();
  };

  if (practicesLoading) {
    return <CoreSpinner />;
  }

  return (
    <>
      <div className="flex flex-col">
        <div className="w-full flex items-center">
          {location.state && <CoreBack />}
          <h2>{t("ManagePractices")}</h2>
        </div>

        <div className="flex flex-row -mt-16 justify-end space-x-2">
          {Object.entries(filter)?.map(
            ([k, v]) =>
              v !== "" && (
                <CoreChip
                  key={v}
                  label={labelForFilterPractice(
                    k,
                    v,
                    practiceServicesData,
                    adminStudio,
                    operatorsData,
                    companiesList
                  )}
                  variant="outlined"
                  onDelete={() => handleRemoveFilter(v)}
                />
              )
          )}

          <GestisciPraticaFilter
            filter={filter}
            setFilter={setFilter}
            services={practiceServicesData}
            isServiceLoading={loadingService}
            slice={practiceManagementSlice}
            studios={adminStudio}
            setDebouncedInputStudioValue={setDebouncedInputStudioValue}
            operators={operatorsData}
            setDebouncedInputOperatorValue={setDebouncedInputOperatorValue}
            companies={companiesList}
            isCompanyLoading={isCompanySelectFetching || isCompanySelectLoading}
            setDebouncedInputCompanyValue={setDebouncedInputCompanyValue}
          />
        </div>

        <div className="flex flex-col">
          <div>
            <div className="flex flex-row justify-between mt-10 items-center">
              {/* Controls */}
              <div className="flex flex-row justify-between items-center w-2/3 space-x-4">
                <UserFilter
                  selectedUser={selectedUser}
                  setSelectedUser={setSelectedUser}
                  usersList={usersData?.Data?.items || []}
                  setDebouncedInputUserValue={setDebouncedInputUserValue}
                  isUsersLoading={isUsersLoading || isUsersFetching}
                  setPage={setPage}
                  externalPlaceholder={`${t("ExtSearchUser")}`}
                />

                <div className="w-full">
                  <GestisciPraticaButtonCockpit
                    onRefetch={handleRefecthDocuments}
                  />
                </div>
              </div>
            </div>
            <GestionePraticheTable
              page={page}
              setPage={setPage}
              data={practicesData?.Data}
              valueToOrderBy={valueToOrderBy}
              setValueToOrderBy={setValueToOrderBy}
              orderDirection={orderDirection}
              setOrderDirection={setOrderDirection}
              isLoading={practicesLoading || practicesFetching}
              totalPages={Math.ceil(
                practicesData?.Data?.count / TABLE_ROWS_NUMBER || 1
              )}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default GestisciPratichePage;
